import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { RootState } from '@/index';
import { morgengrauen } from '@/config/colors';
import commonApi from '@/apis/commonApi';
import { format } from 'date-fns';
import { useParams } from 'react-router-dom';
import { utcToZonedTime } from 'date-fns-tz';

export const useStyles = makeStyles(() => ({
  detailsCard: {
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingLeft: '16px',
    marginBottom: '32px'
  },
  detailsIcon: {
    marginRight: '16px'
  },
  detailsHeader: {
    fontWeight: '500'
  },
  recordItem: {
    display: 'grid',
    minWidth: '550px',
    gridTemplateColumns: '20% 60% 20%',
    '&:not(:last-child)': {
      marginBottom: '8px'
    }
  },
  infoCard: {
    maxHeight: '600px',
    paddingTop: '40px'
  },
  vehiclesContainer: {
    maxHeight: '500px',
    overflowY: 'scroll',
    overflowX: 'hidden'
  },
  priosContainer: {
    maxHeight: '350px',
    overflowY: 'scroll',
    overflowX: 'hidden'
  },
  cardTitle: {
    display: 'flex',
    paddingLeft: '24px',
    borderBottom: `1px solid ${morgengrauen}`,
    alignItems: 'center',
    height: '48px',
  },
  outerContainer: {
    height: 'fit-content',
    border: `1px solid ${morgengrauen}`,
    borderRadius: '2px'
  },
  doubleContainer: {
    display: 'flex',
    paddingBottom: '24px',
    paddingRight: '16px',
    paddingLeft: '16px',
    '&:nth-of-type(2)': {
      borderTop: `1px solid ${morgengrauen}`,
      paddingTop: '32px'
    }
  },
  verticalHeader: {
    fontSize: '0.8rem',
    padding: '5px',
    writingMode: 'vertical-rl',
    transform: 'scale(-1)',
    textAlign: 'center',
    backgroundColor: '#eeeeee',
    borderRadius: '2px'
  },
  sum: {
    marginLeft: '16px',
    display: 'flex',
    alignItems: 'center'
  },
  dataLabel: {
    display: 'flex',
    alignItems: 'center',
  },
  noOverflow: {
    overflow: 'hidden'
  }
}));

export const Dashboard = () => {
  const [accessToken, setAccessToken] = useState<string>('');
  const { selected_process } = useSelector((state: RootState) => state.core);
  const { processId } = useParams();
  const { site_name } = useSelector((state: RootState) => state.login);
  const { planningStatusCode } = useSelector((state: RootState) => state.planning);
  useEffect(() => {
    if (!processId || !site_name) return;

    const requestMetabaseDashboard = async () => {
      try {
        const dateInBerlin = utcToZonedTime(
          new Date(selected_process.timestamp),
          'Europe/Berlin'
        );
        const formattedDate = format(dateInBerlin, 'yyyy-MM-dd');
        const { token } = await commonApi.getMetabaseDashboard({
          dashboard: 397,
          site_name,
          optimization_id: selected_process?.optimization_id,
          date: formattedDate,
        });
        setAccessToken(token);
      } catch (error) {
        console.error('Failed to fetch Metabase token:', error);
      }
    };
    if (planningStatusCode === 200 && selected_process?.optimization_id) {
      requestMetabaseDashboard();
    } else {
      setAccessToken('');
    requestMetabaseDashboard();
    }
  }, [processId, site_name, planningStatusCode]);
  const iframeKey = accessToken ? accessToken : selected_process?.optimization_id;
  return (
    // Iframe to display the dashboard
    <iframe
      key={iframeKey}
      width="100%"
      height="100%"
      style={{ border: 'none', display: 'block' }}
      sandbox="allow-scripts allow-same-origin allow-downloads"
      src={`https://metabase.shared.smartlane.io/embed/dashboard/${accessToken}?#theme=transparent&bordered&titled=false`}
      title='Metabase Dashboard'
    />
  );
};